import { Search, FileText, Send, MessagesSquare, Trophy } from "lucide-react";
import { motion } from "framer-motion";

const steps = [
  {
    n: "01",
    title: "Structured screening",
    desc: "We assess your skills, projects and goals through a focused interview. Only candidates we believe in get onboarded.",
    icon: Search,
    span: "md:col-span-7",
    testid: "step-screening",
  },
  {
    n: "02",
    title: "Resume polish & positioning",
    desc: "Crafted to match the bar of the roles you actually want - not a generic template.",
    icon: FileText,
    span: "md:col-span-5",
    testid: "step-resume",
  },
  {
    n: "03",
    title: "Direct HR outreach",
    desc: "We send your profile to the right point-of-contact at responsive companies. No portals. No black holes.",
    icon: Send,
    span: "md:col-span-5",
    testid: "step-outreach",
  },
  {
    n: "04",
    title: "Interviews, direct with the HR",
    desc: "The HR reaches out to you directly to schedule. No middleman, no back-and-forth - you just take the interview.",
    icon: MessagesSquare,
    span: "md:col-span-4",
    testid: "step-interviews",
  },
  {
    n: "05",
    title: "Placed at the right role",
    desc: "The match that fits - your stack, your salary band, your career trajectory. Sealed and delivered.",
    icon: Trophy,
    span: "md:col-span-3",
    testid: "step-placed",
  },
];

export const HowItWorks = () => {
  return (
    <section id="how-it-works" data-testid="how-it-works-section" className="py-24 sm:py-32">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 md:px-12">
        <div className="max-w-3xl mb-16">
          <p className="label-eyebrow text-[#143628] mb-5">How it works</p>
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            A bespoke path from <em className="italic text-[#143628]">searching</em> to{" "}
            <em className="italic text-[#143628]">signed.</em>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.n}
                data-testid={s.testid}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                className={`group bg-white border border-[#1C1C1A]/10 p-7 sm:p-8 hover:border-[#143628]/40 transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(20,54,40,0.35)] ${s.span}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-serif-display text-4xl text-[#143628]">{s.n}</span>
                  <Icon className="h-5 w-5 text-[#4A4A48] group-hover:text-[#143628] transition-colors" />
                </div>
                <h3 className="font-serif-display text-2xl leading-tight text-[#1C1C1A] mb-3">
                  {s.title}
                </h3>
                <p className="text-[#4A4A48] leading-relaxed text-[15px]">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
